import type { ReactNode } from 'react'

interface Section {
  header: string
  rows: Row[]
}

interface Row {
  header: string
  tooltip?: string
  cells: ReactNode[]
  subRows?: SubRow[]
}

type SubRow = Omit<Row, `subRows`>

const sections = (): Section[] => {
  return [
    {
      header: `Complete view of your customer`,
      rows: [
        {
          header: `Identity Resolution`,
          tooltip: `Unify user history across web, mobile, server, data warehouse, and third-party touchpoints into one comprehensive profile.`,
          cells: [true, true, true],
        },
        {
          header: `Profile API`,
          tooltip: `Query unified profiles to empower internal teams with customer data, and build real time in-app recommendations & personalized campaigns.`,
          cells: [true, true, true],
        },
        {
          header: `Profiles Sync`,
          tooltip: `Access real-time profile data in your warehouse to build data and ML models to lower acquisition costs, increase lifetime value, and more.`,
          cells: [true, true, true],
        },
        {
          header: `Profile Explorer UI`,
          tooltip: `Enable any technical or business team within your organization to explore unified profiles in one place. `,
          cells: [true, true, true],
        },
        {
          header: `Traits`,
          tooltip: `Created more personalized customer experiences by appending calculated and queried customer data to unified profiles.`,
          cells: [false, true, true],
        },
        {
          header: `Predictive traits`,
          tooltip: `Predict customer behavior using a no-code builder to power more timely, targeted, and precise personalized experiences on every channel.`,
          cells: [false, true, true],
        },
      ],
    },
    {
      header: `Build personalized experiences with Twilio Engage`,
      rows: [
        {
          header: `Audiences`,
          tooltip: `Equip technical and business users to group unified profiles with an easy-to-use UI based on customer traits or interactions.`,
          cells: [false, false, true],
        },
        {
          header: `Generative Audiences`,
          tooltip: `Create and launch complex audiences in minutes with a simple text prompt using generative AI`,
          cells: [false, false, true],
        },
        {
          header: `Journeys`,
          tooltip: `Orchestrate and optimize personalized journeys to automate messaging across multiple channels based on real-time customer behavior.`,
          cells: [false, false, true],
        },
      ],
    },
    {
      header: `Collect and send data anywhere`,
      rows: [
        {
          header: `Sources`,
          tooltip: `Collect the full customer view via one API and 450+ pre-built integrations across your apps, sales, support, payment & messaging experiences.`,
          cells: [true, true, true],
        },
        {
          header: `Destinations`,
          tooltip: `Send your first-party data to 450+ tools including data warehouse, product analytics, advertising, email, and more.`,
          cells: [true, true, true],
        },
        {
          header: `Functions`,
          tooltip: `Create custom sources and destinations with just 10 lines of JavaScript, to solve for unique use cases.`,
          cells: [true, true, true],
        },
        {
          header: `Reverse ETL`,
          tooltip: `Activate data from the warehouse into 450+ tools for product analytics, advertising, email, and more.`,
          cells: [true, true, true],
        },
        {
          header: `Developer Tools`,
          tooltip: `Set up, test, and swap new tools with a common UI and data observability.`,
          cells: [true, true, true],
        },
        {
          header: `Public API`,
          tooltip: `Programmatically manage Segment workspaces, sources, destinations, and more via API.`,
          cells: [true, true, true],
        },
      ],
    },
    {
      header: `Usage and billing`,
      rows: [
        {
          header: `Monthly tracked users`,
          tooltip: `The number of anonymous and logged-in visitors that you track with Segment.`,
          cells: [`Custom volume`, `Custom volume`, `Custom volume`],
          subRows: [
            {
              header: `Throughput Limit`,
              tooltip: `Your customer data comes in the form of API Calls and Objects. CDP plans are eligible for custom limits.`,
              cells: [`Custom`, `Custom`, `Custom`],
            },
          ],
        },
        {
          header: `Seats`,
          tooltip: `The number of teammates that can access your Segment workspace.`,
          cells: [`Unlimited`, `Unlimited`, `Unlimited`],
        },
        {
          header: `Payment method`,
          cells: [`Invoice`, `Invoice`, `Invoice`],
        },
        {
          header: `Contract length`,
          cells: [`Unlimited`, `Unlimited`, `Unlimited`],
        },
      ],
    },
    {
      header: `Support`,
      rows: [
        {
          header: `Email/web support`,
          cells: [true, true, true],
        },
        {
          header: `On-demand videos and documentation`,
          tooltip: `Get up and running quickly with developer documentation and on-demand training.`,
          cells: [true, true, true],
        },
        {
          header: `Dedicated support`,
          cells: [`Available`, `Available`, `Available`],
          subRows: [
            {
              header: `Customer Success Manager`,
              tooltip: `Customer Success Manager may be included based on your annual agreement. Please ask your Account Executive for details.`,
              cells: [`Available`, `Available`, `Available`],
            },
            {
              header: `Advisory Services or Professional Services`,
              tooltip: `Custom advisory services and professional services packages available. Please ask your Account Executive for details.`,
              cells: [`+ Add-on`, `+ Add-on`, `+ Add-on`],
            },
          ],
        },
        {
          header: `Uptime SLA`,
          tooltip: `Guaranteed API uptime.`,
          cells: [true, true, true],
        },
      ],
    },
    {
      header: `Security`,
      rows: [
        {
          header: `Account security`,
          tooltip: `Protect your workspace with personalized security settings`,
          cells: [true, true, true],
          subRows: [
            {
              header: `Multi-factor authentication`,
              tooltip: `Provide an additional layer of security beyond your username and password by requiring secondary authentication.`,
              cells: [true, true, true],
            },
            {
              header: `Single sign-on`,
              tooltip: `Centrally manage your policies for access with single sign-on (SSO).`,
              cells: [true, true, true],
            },
            {
              header: `Granular roles and user permissions`,
              tooltip: `Control access to your sources and workspaces with fine-grained permissions to manage how your teammates interact with your data.`,
              cells: [true, true, true],
            },
          ],
        },
        {
          header: `Certifications`,
          tooltip: `To learn more about Segment's security and privacy program, and certifications and attestations.`,
          cells: [true, true, true],
        },
        {
          header: `HIPAA-eligibility`,
          cells: [`Available`, `Available`, `Available`],
          tooltip: `Store and manage PHI within your workspace.`,
        },
        {
          header: `Regional Segment (EU or US)`,
          tooltip: `Segment offers regional infrastructure in both Europe and the US.`,
          cells: [true, true, true],
        },
      ],
    },
  ]
}

export default sections
